import { useCallback, useEffect, useMemo, useState } from "react"
import {
  useGetPaymentRequestLineCapacityQuery,
  useGetPaymentRequestBudgetAllocationsQuery,
} from "shared/redux/endpoints/procurementPayments"
import useDebouncedValue from "../../../hooks/useDebouncedValue"
import { fromAllocationRow } from "../budgetAllocation"
import {
  seedFromCapacity,
  seedFromStored,
  editLine,
  editExtra,
  addExtra,
  removeExtra,
  allocatedOf,
  allocationIssue,
  linesFull,
  toBodyLines,
} from "./lineAllocation"

// Fetches one PO's line capacity for the amount being paid and hands it up.
// Changing the amount refetches, which resets that PO to BE's default split.
const PoLineCapacitySync = ({ purchaseId, amount, excludePaymentRequestId, onCapacity }) => {
  const debounced = useDebouncedValue(amount, 400)
  const { data, isError } = useGetPaymentRequestLineCapacityQuery(
    { purchaseId, amount: debounced, excludePaymentRequestId },
    { skip: debounced === "" || debounced == null }
  )
  useEffect(() => {
    if (data) onCapacity(purchaseId, data)
  }, [data, purchaseId, onCapacity])
  useEffect(() => {
    if (isError) onCapacity(purchaseId, null)
  }, [isError, purchaseId, onCapacity])
  return null
}

const sumValues = (rows) => rows.reduce((s, r) => s + (Number(r.value) || 0), 0)

// Per-PO line split for a PO-linked payment on the create/edit form.
// `editId` (edit form) excludes this PR from the capacity and prefills from
// what it stored; a PO whose capacity call failed is left to BE's default
// split (no `lines` sent for it). The PO's payment amount follows lines +
// extras, reported through `onAmountChange(purchaseId, amount)` — except on
// rows with `amountsLocked`, where the amount stays put.
const usePoLineAllocations = ({ payingRows, enabled, editId, onAmountChange }) => {
  const [states, setStates] = useState({})
  const [failed, setFailed] = useState({})

  const { data: storedRaw, isFetching: storedLoading } = useGetPaymentRequestBudgetAllocationsQuery(editId, {
    skip: !enabled || !editId,
  })
  const stored = useMemo(() => (storedRaw || []).map((r) => fromAllocationRow(r)), [storedRaw])

  // The saved split is reused only while the PO's amount is still what it
  // was saved with; any other amount starts from BE's suggestion. An amount
  // that the lines themselves produced (state.total already equals it) keeps
  // the typed split instead of resetting it.
  const onCapacity = useCallback(
    (purchaseId, capacity) => {
      setFailed((prev) => ({ ...prev, [purchaseId]: !capacity }))
      if (!capacity) return
      const own = stored.filter((r) => r.purchaseId === String(purchaseId))
      const useStored = own.some((r) => r.allocationSource === "STORED") && sumValues(own) === Number(capacity.amount)
      setStates((prev) => {
        if (prev[purchaseId]?.total === Number(capacity.amount)) return prev
        return { ...prev, [purchaseId]: useStored ? seedFromStored(capacity, own) : seedFromCapacity(capacity) }
      })
    },
    [stored]
  )

  const lockedIds = new Set(payingRows.filter((r) => r.amountsLocked).map((r) => r.purchaseId))

  const update = (purchaseId, recipe) => {
    const current = states[purchaseId]
    if (!current) return
    const recipeResult = recipe(current)
    const next = lockedIds.has(purchaseId) ? { ...recipeResult, total: current.total } : recipeResult
    setStates((prev) => ({ ...prev, [purchaseId]: next }))
    if (next.total !== current.total) onAmountChange(purchaseId, next.total)
  }

  const editor = {
    onLine: (purchaseId, orLineId, value) => update(purchaseId, (s) => editLine(s, orLineId, value)),
    onExtra: (purchaseId, key, patch) => update(purchaseId, (s) => editExtra(s, key, patch)),
    onAddExtra: (purchaseId) => update(purchaseId, addExtra),
    onRemoveExtra: (purchaseId, key) => update(purchaseId, (s) => removeExtra(s, key)),
    statusOf: (purchaseId) => {
      const s = states[purchaseId]
      if (!s || failed[purchaseId]) return null
      return {
        allocated: allocatedOf(s),
        total: s.total,
        paidByOthers: s.paidByOthers,
        pendingByOthers: s.pendingByOthers,
        issue: allocationIssue(s),
        canAddExtra: linesFull(s),
      }
    },
  }

  const syncs =
    enabled && !(editId && storedLoading)
      ? payingRows.map((row) => (
          <PoLineCapacitySync
            key={row.purchaseId}
            purchaseId={row.purchaseId}
            amount={row.amount}
            excludePaymentRequestId={editId}
            onCapacity={onCapacity}
          />
        ))
      : null

  const ready = (row) => {
    const s = states[row.purchaseId]
    return s && s.total === Number(row.amount)
  }

  // Blocks saving while a PO's split is loading or doesn't add up.
  const issue = () => {
    if (!enabled) return null
    for (const row of payingRows) {
      if (!failed[row.purchaseId]) {
        const label = row.poCode || "Purchase order"
        if (!ready(row)) return `${label}: budget allocation is still loading. Please wait a moment.`
        const problem = allocationIssue(states[row.purchaseId])
        if (problem) return `${label}: ${problem}`
      }
    }
    return null
  }

  const linesFor = (row) => (!failed[row.purchaseId] && ready(row) ? toBodyLines(states[row.purchaseId]) : undefined)

  // PUT must list every PO, or BE resets the PR to its default split.
  const allLines = () => {
    const perPo = payingRows.map(linesFor)
    return perPo.every(Boolean) ? perPo.flat() : undefined
  }

  return { states: enabled ? states : {}, editor: enabled ? editor : undefined, syncs, issue, linesFor, allLines }
}

export default usePoLineAllocations
